import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useUser } from './UserContext';

const StatsContext = createContext();

export const useStats = () => {
  const context = useContext(StatsContext);
  if (!context) {
    throw new Error('useStats must be used within a StatsProvider');
  }
  return context;
};

export const StatsProvider = ({ children }) => {
  const { user, token, isPremium, isAdmin, isBetatester } = useUser();
  const userId = user?.user_id || user?.id;
  const isFreeDebug = user?.role === "free-debug" || user?.subscription_status === "free-debug";
  const canSeeAreaStats = isPremium || isAdmin || isBetatester || isFreeDebug;
  const [stats, setStats] = useState(null);
  const [areaStats, setAreaStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Clear stats when user logs out (user becomes null)
  React.useEffect(() => {
    if (!userId) {
      setStats(null);
      setAreaStats(null);
    }
  }, [userId]);

  const refreshStats = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const statsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stats/${userId}`, { headers });
      setStats(statsRes.data);
      
      // Fetch area stats for premium/admin/betatester/free-debug users
      if (canSeeAreaStats) {
        const areaRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stats/areas/${userId}`, { headers });
        setAreaStats(areaRes.data);
      } else {
        setAreaStats(null);
      }
    } catch (e) {
      console.error('[StatsContext] Error fetching stats:', e);
    } finally {
      setLoading(false);
    }
  }, [userId, token, canSeeAreaStats]);

  return (
    <StatsContext.Provider value={{ stats, areaStats, loading, refreshStats }}>
      {children}
    </StatsContext.Provider>
  );
};
